import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { getBasketData } from 'services/getBasketData';
import { transitionBasketStatus } from 'services/basketsApi';
import { FetchGivingBasketPayload, GivingBasketState, UpdateBasketStatus } from 'types/baskets';
import { EventStatus } from 'types/common';
import { selectSelectedParishId } from 'redux/selectors/parishContext';

type GivingState = GivingBasketState & { loadedParishId: string | null };

const initialState: GivingState = {
    activeRecords: [],
    inActiveRecords: [],
    loading: false,
    error: null,
    loadMore: false,
    loadedParishId: null,
};
export const getStatus = (status: EventStatus) => (Array.isArray(status) ? status[0] : status);

function resolveParishId(getState: () => unknown, parishId?: string, companyId?: string) {
    if (parishId) return parishId;
    if (companyId) return companyId;
    return selectSelectedParishId(getState() as any);
}

export const updateBasketStatus = createAsyncThunkWrapper<any, UpdateBasketStatus>(
    'update/updateBasketStatus',
    async ({ status, id }, { getState }) => {
        const parishId = selectSelectedParishId(getState() as any);
        if (!parishId) throw new Error('No selected parish');
        return transitionBasketStatus(parishId, String(id), status);
    }
);

export const fetchGivingBasket = createAsyncThunkWrapper<any, FetchGivingBasketPayload>(
    'giving/fetchGivingBasket',
    async ({ status, subType, headers, name, companyId, parishId }: FetchGivingBasketPayload, { getState }) => {
        const { pageSize, pageNumber } = headers;
        const resolvedParishId = resolveParishId(getState, parishId, companyId);
        if (!resolvedParishId) {
            return;
        }
        const response = await getBasketData({
            status,
            subType,
            parishId: resolvedParishId,
            headers,
            name,
        });
        if (!response) return;
        return { response, pageNumber, pageSize, status, parishId: resolvedParishId };
    }
);

const givingBasketSlice = createSlice({
    name: 'givingBasket',
    initialState,
    reducers: {
        resetGivingBaskets: () => ({ ...initialState }),
    },
    extraReducers: (builder) => {
        builder.addCase(fetchGivingBasket.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchGivingBasket.fulfilled, (state, action) => {
            state.loading = false;
            const { payload } = action || {};
            if (!Array.isArray(payload?.response?.events)) return;

            const { response, pageNumber, pageSize = 0, status = 'ACTIVE', parishId } = payload || {};
            const { events, loadMore, total } = response || {};
            // Stale load-more after parish switch (before page reset lands).
            if (pageNumber !== 0 && state.loadedParishId !== parishId) return;

            if (typeof loadMore === 'boolean') {
                state.loadMore = loadMore;
            } else {
                state.loadMore = typeof total === 'number' ? (pageNumber + 1) * pageSize < total : events?.length >= pageSize;
            }

            const keyToUpdate = getStatus(status) === 'PENDING' ? 'inActiveRecords' : 'activeRecords';
            if (pageNumber === 0) {
                state.loadedParishId = parishId;
                state.activeRecords = [];
                state.inActiveRecords = [];
                state[keyToUpdate] = events || [];
            } else {
                state[keyToUpdate] = [...(state?.[keyToUpdate] || []), ...(events || [])];
            }
        });
        builder.addCase(fetchGivingBasket.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || null;
        });
    },
});

export const { resetGivingBaskets } = givingBasketSlice.actions;
export default givingBasketSlice.reducer;
